export type PriceMatchStatus = 'PENDING' | 'APPROVED' | 'DECLINED'

export interface PriceMatchChecklistItem {
  id: string
  label: string
  passed: boolean
}

export interface PriceMatchFileResponse {
  url?: string
  fileUuid?: string
  mimetype?: string
  keyFromCloudProvider?: string
  fileCloudProvider?: string
  fileCategory?: string
}

export interface PriceMatchOrderResponse {
  resourceId: string
  referenceNumber: string
  product: string
  costMajor: number
  price: number
  quantity: number
  dispensedQuantity: number | null
  currency: string
  paymentMethod: string
  unitOfMeasure: string
  fulfillmentCode: string
  verificationCode: string
  status: string
  paymentStatus: string
  isReconcilable: boolean
  stationId: string
  createdAt: string
}

export interface PriceMatchUserResponse {
  resourceId?: string
  firstName?: string
  lastName?: string
  email?: string
}

export interface PriceMatchApiResponse {
  resourceId: string
  status: PriceMatchStatus
  image?: PriceMatchFileResponse | null
  note?: string
  latitude?: number
  longitude?: number
  address?: string
  priceDifferenceMajor?: number
  refundAmountMajor?: number
  rejectionNote?: string
  reviewedAt?: string
  orderId?: number
  order: PriceMatchOrderResponse
  user?: PriceMatchUserResponse | null
  createdAt?: string
  reviewedByAdminEmail: string
}

export interface PriceMatchListApiEnvelope {
  data?:
    | PriceMatchApiResponse[]
    | {
        dataset?: PriceMatchApiResponse[]
        meta?: { total?: number; pagination?: { total?: number } }
      }
  dataset?: PriceMatchApiResponse[]
  total?: number
  meta?: {
    total?: number
    pagination?: {
      total?: number
    }
  }
}

export interface PriceMatchSubmission {
  id: string
  orderId: string
  customer: string
  product: string
  purchasePrice: string
  submittedAt: string
  status: PriceMatchStatus
  captureLocation: string
  distanceFromBoost: string
  additionalNotes: string
  reviewNotes?: string
  rejectionReason?: string
  approvedBy?: string
  approvalDateTime?: string
  walletCreditAmount?: string
  rejectedBy?: string
  rejectionNote?: string
  rejectionDateTime?: string
  checklist: PriceMatchChecklistItem[]
  imageUrl?: string
  priceDifferenceMajor: number
  refundAmountMajor: number
  createdAtRaw?: string
  reviewedAtRaw?: string
  latitude?: number
  longitude?: number
  reviewedByAdminEmail: string
  reviewedAt?: string
  order?: {
    resourceId: string
    referenceNumber: string
    product: string
    costMajor: number
    price: number
    quantity: number
    dispensedQuantity: number | null | undefined
    currency: string
    paymentMethod: string
    unitOfMeasure: string
    fulfillmentCode: string
    verificationCode: string
    status: string
    paymentStatus: string
    isReconcilable: boolean
    stationId: string
    createdAt: string
  }
}

const baseChecklist: PriceMatchChecklistItem[] = [
  { id: 'image-clear', label: 'Image clear', passed: true },
  { id: 'station-visible', label: 'Station name visible', passed: true },
  { id: 'price-visible', label: 'Fuel price visible', passed: true },
  { id: 'product-visible', label: 'Product visible', passed: true },
  { id: 'location-proximity', label: 'Location Proximity', passed: true },
]

export const formatPriceMatchCurrency = (amount = 0) =>
  `₦${amount.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`

export const formatPriceMatchPurchasePrice = (
  price?: number,
  unitOfMeasure?: string,
  currency = '₦',
) => {
  const major = price ?? 0
  const unitLabel = unitOfMeasure ? `/${unitOfMeasure === 'Litre' ? 'L' : unitOfMeasure}` : ''
  return `${currency}${major.toLocaleString()}${unitLabel}`
}

export const formatPriceMatchDate = (date?: string) => {
  if (!date) return ''

  const value = new Date(date)
  if (Number.isNaN(value.getTime())) return ''

  return value.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export const getPriceMatchStatusLabel = (status: PriceMatchStatus) => {
  if (status === 'PENDING') return 'Pending'
  if (status === 'APPROVED') return 'Approved'
  return 'Declined'
}

const parseRejectionNote = (note?: string) => {
  if (!note) {
    return {
      reason: undefined,
      reviewNotes: undefined,
    }
  }

  if (note.includes('||')) {
    const [reason, reviewNotes] = note.split('||').map((item) => item.trim())
    return {
      reason: reason || undefined,
      reviewNotes: reviewNotes || undefined,
    }
  }

  return {
    reason: note,
    reviewNotes: note,
  }
}

export const mapPriceMatchSubmission = (payload: PriceMatchApiResponse): PriceMatchSubmission => {
  const rejectionDetails = parseRejectionNote(payload.rejectionNote)
  const firstName = payload.user?.firstName?.trim() ?? ''
  const lastName = payload.user?.lastName?.trim() ?? ''
  const customerName = `${firstName} ${lastName}`.trim() || payload.user?.email || 'N/A'

  const purchaseCurrency =
    payload.order?.currency === 'NGN' || !payload.order?.currency ? '₦' : payload.order.currency

  return {
    id: payload.resourceId,
    orderId:
      payload.order?.referenceNumber ||
      (payload.orderId !== undefined
        ? String(payload.orderId)
        : payload.order?.resourceId || 'N/A'),
    customer: customerName,
    product: payload.order?.product || 'N/A',
    purchasePrice: formatPriceMatchPurchasePrice(
      payload.order?.price ?? payload.order?.costMajor,
      payload.order?.unitOfMeasure,
      purchaseCurrency,
    ),
    submittedAt: formatPriceMatchDate(payload.createdAt) || 'N/A',
    status: payload.status,
    captureLocation: payload.address || 'N/A',
    distanceFromBoost: 'N/A',
    additionalNotes: payload.note || 'No additional note provided.',
    reviewNotes: rejectionDetails.reviewNotes,
    rejectionReason: rejectionDetails.reason,
    approvedBy: payload.status === 'APPROVED' ? 'Admin User' : undefined,
    approvalDateTime:
      payload.status === 'APPROVED' ? formatPriceMatchDate(payload.reviewedAt) : undefined,
    walletCreditAmount:
      payload.status === 'APPROVED'
        ? formatPriceMatchCurrency(payload.refundAmountMajor ?? 0)
        : undefined,
    rejectedBy: payload.status === 'DECLINED' ? 'Admin User' : undefined,
    rejectionNote: payload.status === 'DECLINED' ? rejectionDetails.reason : undefined,
    rejectionDateTime:
      payload.status === 'DECLINED' ? formatPriceMatchDate(payload.reviewedAt) : undefined,
    checklist: baseChecklist,
    imageUrl: payload.image?.url,
    priceDifferenceMajor: payload.priceDifferenceMajor ?? 0,
    refundAmountMajor: payload.refundAmountMajor ?? 0,
    createdAtRaw: payload.createdAt,
    reviewedAtRaw: payload.reviewedAt,
    latitude: payload.latitude,
    longitude: payload.longitude,
    reviewedByAdminEmail: payload.reviewedByAdminEmail,
    reviewedAt: payload.reviewedAt,
    order: payload.order,
  }
}

export const extractPriceMatchList = (
  responseData: PriceMatchListApiEnvelope,
): PriceMatchApiResponse[] => {
  const data = responseData?.data

  if (Array.isArray(data)) return data
  if (Array.isArray(data?.dataset)) return data?.dataset || []
  if (Array.isArray(responseData?.dataset)) return responseData?.dataset || []
  return []
}

export const extractPriceMatchTotal = (
  responseData: PriceMatchListApiEnvelope,
  fallbackLength: number,
) => {
  const meta = responseData?.meta
  const nestedData = responseData?.data
  const nestedMeta = !Array.isArray(nestedData) ? nestedData?.meta : undefined
  const pagination = meta?.pagination ?? nestedMeta?.pagination

  return (
    pagination?.total ?? meta?.total ?? nestedMeta?.total ?? responseData?.total ?? fallbackLength
  )
}
